import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

/**
 * Turns whatever a query rejected with into one sentence a person can read. RTK
 * Query hands back a union of two unrelated error shapes, which every error state
 * in the app would otherwise narrow itself.
 *
 * **The service's own message is only used for a 4xx.** A 404 names the path the
 * caller supplied, which is useful and safe; passing a 5xx body through is how a
 * table name reaches a screenshot.
 */
export const describeQueryError = (
  error: FetchBaseQueryError | SerializedError | undefined,
): string => {
  if (error === undefined) {
    return 'Something went wrong.';
  }

  // SerializedError: something threw rather than the request failing.
  if (!('status' in error)) {
    return error.message ?? 'Something went wrong.';
  }

  if (error.status === 'FETCH_ERROR') {
    return 'Could not reach the content service. Check your connection and try again.';
  }

  if (error.status === 'PARSING_ERROR' || error.status === 'CUSTOM_ERROR') {
    return error.error;
  }

  if (error.status === 'TIMEOUT_ERROR') {
    return 'The content service took too long to answer. Try again.';
  }

  if (error.status === 401) {
    return 'You are not signed in, or your session has expired.';
  }

  if (error.status === 403) {
    return 'You do not have access to this content.';
  }

  if (error.status === 404) {
    return 'That content does not exist.';
  }

  if (typeof error.status === 'number' && error.status < 500) {
    return serviceMessage(error.data) ?? 'That request could not be completed.';
  }

  return 'The content service is having a problem. Try again shortly.';
};

/** The `{ error: { code, message } }` envelope, if that is what came back. */
const serviceMessage = (body: unknown): string | undefined => {
  if (typeof body !== 'object' || body === null || !('error' in body)) {
    return undefined;
  }

  const { error } = body;

  if (typeof error !== 'object' || error === null || !('message' in error)) {
    return undefined;
  }

  const { message } = error;

  return typeof message === 'string' ? message : undefined;
};

/**
 * The sentence a content page shows under "Unable to load this <thing>". **The
 * design's wording, one sentence for every status code** — a 500, a timeout, a
 * dropped connection and a mistyped URL are the same event from the reader's
 * side.
 *
 * **401 and 403 are the exception**, because each has a different fix and
 * neither is "try again": telling someone to wait when they need to sign in,
 * or when they never had access at all, costs them ten minutes for nothing.
 */
export const describeContentError = (
  error: FetchBaseQueryError | SerializedError | undefined,
  noun: string,
): string => {
  const isAuthError =
    error !== undefined && 'status' in error && (error.status === 401 || error.status === 403);

  return isAuthError
    ? describeQueryError(error)
    : `We couldn't retrieve the data for this ${noun}. ` +
        'This might be a temporary issue — please try again in a moment.';
};
